import React, { FunctionComponent, useEffect, useState } from 'react';
import { globalHistory } from '@reach/router';
import { SbBlokData } from '@storyblok/react';
import { useBreadcrumb } from 'gatsby-plugin-breadcrumb';

import { Content, Heading, P } from '@savebetter/components';

import { getCrumbLabel, getCrumbName, getCrumbs } from '../utils';

import Breadcrumbs from './Breadcrumbs';
import { Global } from './Global';
import { MediaProps } from './Media';
import { RichText } from './RichText';
import { Section } from './Section';
import { SEOv2 } from './SeoV2';
import { renderComponent } from '.';

interface Blok extends SbBlokData {
  title: string;
  image: MediaProps[];
  intro?: string;
  body: SbBlokData[];
  blogPostEndingSection?: SbBlokData[];
  media: MediaProps[];
  heading: string;
  text: string;
  iframeUrl: string;
  layout: GlobalBlok[];
  metadata: { title?: string; description?: string };
  Metadata: { title?: string; description?: string };
  showBreadcrumbs?: boolean;
}

interface BlogHomePageProps {
  blok: Blok;
}

interface GlobalBlok extends SbBlokData {
  header: SbBlokData[];
  footer: SbBlokData[];
  hasNavigation: boolean;
}

export const BlogHomePage: FunctionComponent<React.PropsWithChildren<BlogHomePageProps>> = ({ blok }) => {
  const {
    title = '',
    intro,
    body = [],
    image = [],
    metadata = {},
    Metadata,
    showBreadcrumbs,
    customFooterContent
  } = blok;
  const metaData = Metadata || metadata;
  const [currentCrumb, setCurrentCrumb] = useState('');
  const { location } = globalHistory;
  const { crumbs } = useBreadcrumb({
    location,
    crumbLabel: getCrumbLabel(currentCrumb)
  });
  useEffect(() => {
    setCurrentCrumb(getCrumbName(location?.pathname));
  }, [location]);

  return (
    <Global
      id="d83577d3-bcdc-4d28-a669-46cb050b44ec"
      customFooterContent={<RichText content={customFooterContent} className="text-xs" />}
    >
      <SEOv2 title={metaData.title || ''} image={image} description={metaData.description || ''} />
      <div className="flex flex-col">
        {showBreadcrumbs && (
          <Section className="pt-12">
            <Content>
              <Breadcrumbs currentCrumb={currentCrumb} crumbPaths={getCrumbs(crumbs)} />
            </Content>
          </Section>
        )}
        <Heading className="text-center md:p-10" level="1" kind="2">
          {title}
        </Heading>
        {intro && <P className="text-xl py-5">{intro}</P>}
      </div>

      <div className="relative">
        {body.length > 0 && body.map(block => renderComponent({ key: block._uid, blok: block }, block.component))}
      </div>
    </Global>
  );
};
